import Config from './Config';
import FocusManager from './FocusManager';

class FocusManagerFacade {
    /**
     * Adds an event listener.
     */

    public addEventListener: FocusManager['addEventListener'];

    /**
     * Triggers a `BLUR` input event, competely removing focus from any node within the provider.
     */

    public blur: FocusManager['blur'];

    /**
     * Configures a focus manager instance after it has been instantiated.
     */

    public configure: FocusManager['configure'];

    /**
     * Triggers a `DOWN` input event.
     */

    public down: FocusManager['down'];

    /**
     * Programmatically applies focus to an arbitrary focusable node using its ID or a path of
     * indices from the root focusable container.
     *
     * Alternatively, both an ID of a node to descend from, *and* a path may be supplied.
     *
     * @param idOrPath A string ID or path of indices with which to target a focusable node.
     * @param path An optional path to descend from, if passing an ID as the first argument.
     */

    public focus: FocusManager['focus'];

    /**
     * Triggers an `LEFT` input event.
     */

    public left: FocusManager['left'];

    /**
     * Removes an event listener.
     */

    public removeEventListener: FocusManager['removeEventListener'];

    /**
     * Triggers an `RIGHT` input event.
     */

    public right: FocusManager['right'];

    /**
     * Triggers an `SELECT` input event.
     */

    public select: FocusManager['select'];

    /**
     * Triggers an `UP` input event.
     */

    public up: FocusManager['up'];

    /**
     * A reference to the currently focused node, if a node is in focus.
     */

    public readonly focusedNode!: FocusManager['focusedNode'];

    /**
     * A reference to the root node of the tree of focusable nodes, if present.
     */

    public readonly root!: FocusManager['root'];

    /**
     * A map of all registered focusable nodes, by ID.
     */

    public readonly registeredNodes!: FocusManager['registeredNodes'];

    /**
     * A reference to the focus manager instance's latest configuration.
     */

    public readonly config!: FocusManager['config'];

    constructor(options: Partial<Config>) {
        const focusManager = new FocusManager(options);

        this.addEventListener = focusManager.addEventListener.bind(focusManager);
        this.blur = focusManager.blur.bind(focusManager);
        this.configure = focusManager.configure.bind(focusManager);
        this.down = focusManager.down.bind(focusManager);
        this.focus = focusManager.focus.bind(focusManager);
        this.left = focusManager.left.bind(focusManager);
        this.removeEventListener = focusManager.removeEventListener.bind(focusManager);
        this.right = focusManager.right.bind(focusManager);
        this.select = focusManager.select.bind(focusManager);
        this.up = focusManager.up.bind(focusManager);

        Object.defineProperties(this, {
            root: {
                get: () => focusManager.root,
            },
            registeredNodes: {
                get: () => focusManager.registeredNodes,
            },
            focusedNode: {
                get: () => focusManager.focusedNode,
            },
            config: {
                get: () => focusManager.config,
            },
            _: {
                get: () => focusManager,
            },
        });
    }
}

export default FocusManagerFacade;
